import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Form, Input, Select, Typography, Radio, Button, Space } from "antd";
import styled from "styled-components";
import { SearchOutlined, CloseOutlined, BankOutlined, LoadingOutlined } from "@ant-design/icons";
import { get, debounce } from "lodash";
import container from "container/BrokerDashboard/editAgentContainer";

import DbaSection from "./DbaSection";

import {
  GoogleAutoComplete,
} from "dumbComponents/common/UI/antd"

const { Title } = Typography;
const { Option } = Select;

// Function to extract agent ID from URL
const getAgentIdFromUrl = () => {
  const pathname = window.location.pathname;
  const match = pathname.match(/\/broker\/dashboard\/(\d+)\/orientation-info/);
  return match ? parseInt(match[1]) : null; // return integer if found, null if not found
};

const SuccessMessage = styled.div`
  border-radius: 8px;
  background: var(--Success-600, #16a34a);
  position: fixed;
  right: 79.5px;
  top: 84px;
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: white;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const StyledTag = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: #ffffff;
  color: #1a1a1a;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 14px;
  margin: 4px;
  font-weight: 500;
  border: 1px solid #e0e0e0;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Box = styled.div`
  border: 1px solid #e0e0e0;
  padding: 20px;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 16px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    color: #262626;
    font-weight: 500;
    font-size: 14px;
  }

  .ant-input,
  .ant-select {
    border: 1px solid #d9d9d9;
    border-radius: 8px !important;
    background: #ffffff;
    color: #262626;
    height: 40px !important;

    &:hover {
      border-color: #bfbfbf;
    }

    &:focus {
      border-color: #722ed1;
      box-shadow: 0 0 0 2px rgba(114, 46, 209, 0.1);
    }
  }

  .ant-input[disabled],
  .ant-select-disabled {
    background: #ffffff;
    color: #262626;
    border-color: #d9d9d9;
  }

  .ant-select {
    height: 40px !important;
    border: none !important;

    .ant-select-selector {
      height: 40px !important;
      border-radius: 8px !important;
      padding: 8px 12px !important;
      border: 1px solid #d9d9d9 !important;
      background: #ffffff !important;

      .ant-select-selection-search-input {
        height: 38px !important;
        line-height: 38px !important;
      }

      .ant-select-selection-item {
        line-height: 38px !important;
      }
    }

    &:hover .ant-select-selector {
      border-color: #bfbfbf !important;
    }

    &.ant-select-focused .ant-select-selector {
      border-color: #722ed1 !important;
      box-shadow: 0 0 0 2px rgba(114, 46, 209, 0.1) !important;
    }

    &.ant-select-open .ant-select-selector {
      border-color: #722ed1 !important;
    }
  }

  .ant-select-disabled {
    background: #ffffff !important;
    color: #262626 !important;
    border: none !important;

    .ant-select-selector {
      background: #ffffff !important;
      color: #262626 !important;
      border-color: #d9d9d9 !important;
    }
  }

  .ant-form-item-control {
    margin-bottom: 0 !important;
  }

  margin-bottom: 0 !important;
`;

const US_OBJ = {
  country: { short_name: 'US' }
};

const BusinessInfoSection = ({
  getOnboardingAssociationInfoResponse,
  getOnboardingOfficeInfoResponse,
  getOnboardingDbaInfoResponse,
  updateOnboardingInfo,
  getOnboardingAssociationInfo, // Add this
  getOnboardingOfficeInfo, // Add this
  getOnboardingDbaInfo, 
  agentRole,// Add this
  ...props
}) => {
  const { data: assocData } = getOnboardingAssociationInfoResponse || {};
  const { data: officeData } = getOnboardingOfficeInfoResponse || {};
  const { data: dbaData } = getOnboardingDbaInfoResponse || {};

  const agentId = getAgentIdFromUrl();

  const [form] = Form.useForm();
  const [editMode, setEditMode] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showDba, setShowDba] = useState(false);

  const [associationList, setAssociationList] = useState([]);
  const [mlsList, setMlsList] = useState([]);

  const [associationOptions, setAssociationOptions] = useState([]);
  const [mlsOptions, setMlsOptions] = useState([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [loadingMls, setLoadingMls] = useState(false);
  const [associationSearchText, setAssociationSearchText] = useState("");
  const [mlsSearchText, setMlsSearchText] = useState("");

  const [associationSkip, setAssociationSkip] = useState(0);
  const [associationLimit] = useState(10);
  const [hasMoreAssociations, setHasMoreAssociations] = useState(true);
  const [isLoadingMoreAssociations, setIsLoadingMoreAssociations] = useState(
    false
  );

  const dbaSectionRef = useRef();
  const [dbaPayloadFunction, setDbaPayloadFunction] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleDbaPayloadReady = useCallback((payloadOrFunction) => {
    if (typeof payloadOrFunction === "function") {
      setDbaPayloadFunction(() => payloadOrFunction);
    } else if (payloadOrFunction && payloadOrFunction.createDbaPayload) {
      setDbaPayloadFunction(() => payloadOrFunction.createDbaPayload);
    }
  }, []);

  const fetchAssociations = useCallback(
    async (search = "", skip = 0, limit = 10, append = false) => {
      try {
        if (append) {
          setIsLoadingMoreAssociations(true);
        } else {
          setLoadingAssociations(true);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1.0/onboarding/associations-soul?skip=${skip}&limit=${limit}&search_text=${encodeURIComponent(search)}&agent_id=${agentId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        let associationsData = [];
        let totalCount = 0;

        if (data && data.response && Array.isArray(data.response.data)) {
          associationsData = data.response.data;
          totalCount = data.response.total_count || 0;
        } else if (Array.isArray(data)) {
          associationsData = data;
          totalCount = data.length;
        } else {
          associationsData = [];
          totalCount = 0;
        }

        if (append) {
          setAssociationOptions((prev) => {
            const updated = [...prev, ...associationsData];
            setHasMoreAssociations(updated.length < totalCount);
            return updated;
          });
        } else {
          setAssociationOptions(associationsData);
          setHasMoreAssociations(associationsData.length < totalCount);
        }
      } catch (error) {
        console.error("Error fetching associations:", error);
        if (!append) {
          setAssociationOptions([]);
        }
      } finally {
        if (append) {
          setIsLoadingMoreAssociations(false);
        } else {
          setLoadingAssociations(false);
        }
      }
    },
    [agentId]
  );

  const loadMoreAssociations = useCallback(() => {
    if (!isLoadingMoreAssociations && hasMoreAssociations) {
      const nextSkip = associationSkip + associationLimit;
      setAssociationSkip(nextSkip);
      fetchAssociations(
        associationSearchText,
        nextSkip,
        associationLimit,
        true
      );
    }
  }, [
    isLoadingMoreAssociations,
    hasMoreAssociations,
    associationSkip,
    associationLimit,
    associationSearchText,
    fetchAssociations,
  ]);

  const fetchMls = useCallback(
    async (search = "") => {
      try {
        setLoadingMls(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1.0/fetch-mls-info?searchText=${search}&agent_id=${agentId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        let mlsData = [];
        if (Array.isArray(data)) {
          mlsData = data;
        } else if (data && Array.isArray(data.data)) {
          mlsData = data.data;
        } else if (data && Array.isArray(data.response)) {
          mlsData = data.response;
        } else {
          mlsData = [];
        }

        setMlsOptions(mlsData);
      } catch (error) {
        console.error("Error fetching MLS:", error);
        setMlsOptions([]);
      } finally {
        setLoadingMls(false);
      }
    },
    [agentId]
  );

  const debouncedAssociationSearch = useMemo(
    () => debounce((searchText) => {
      setAssociationSkip(0);
      setHasMoreAssociations(true);
      fetchAssociations(searchText, 0, associationLimit, false);
    }, 300),
    [fetchAssociations, associationLimit]
  );

  const debouncedMlsSearch = useMemo(
    () => debounce((searchText) => {
      fetchMls(searchText);
    }, 300),
    [fetchMls]
  );

  const handleAssociationSearch = useCallback(
    (value) => {
      setAssociationSearchText(value);
      debouncedAssociationSearch(value);
    },
    [debouncedAssociationSearch]
  );

  const handleMlsSearch = useCallback(
    (value) => {
      setMlsSearchText(value);
      debouncedMlsSearch(value);
    },
    [debouncedMlsSearch]
  );

  const handleAssociationDropdownScroll = useCallback(
    (e) => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 5) {
        loadMoreAssociations();
      }
    },
    [loadMoreAssociations]
  );

  useEffect(() => {
    if (agentId) {
      fetchAssociations();
      fetchMls();
    }
  }, [agentId, fetchAssociations, fetchMls]);

  useEffect(() => {
    return () => {
      debouncedAssociationSearch.cancel();
      debouncedMlsSearch.cancel();
    };
  }, [debouncedAssociationSearch, debouncedMlsSearch]);

  useEffect(() => {
    if (assocData) {
      setAssociationList(assocData.associations || []);
      setMlsList(assocData.mlsInfo || []);
    }

    if (officeData) {
      form.setFieldsValue({
        address: officeData.office_address || "",
        apt: officeData.office_suite_no || "",
        city: officeData.office_city || "",
        state: officeData.office_state || "",
        zipcode: officeData.office_zipcode || "",
      });

      // Set showDba based on is_dba_signed from office info response
      if (officeData.is_dba_signed !== undefined) {
        setShowDba(
          officeData.is_dba_signed === 1 || officeData.is_dba_signed === true
        );
      }
    }
  }, [assocData, officeData, dbaData, form]);
  const isRoleAllowed = () => {
    const allowedRoles = ['team_lead', 'co_team_lead', 'admin_agent', 'operation_agent', 'individual_agent'];
    return allowedRoles.includes(agentRole?.toLowerCase());
  };

  useEffect(() => {
    getOnboardingAssociationInfo({
      agentId: agentId,
      step: "association_info",
    });
    getOnboardingOfficeInfo({ agentId: agentId, step: "office_info" });
    getOnboardingDbaInfo({ agentId: agentId, step: "dba_info" });
  }, [
    getOnboardingAssociationInfo,
    getOnboardingOfficeInfo,
    getOnboardingDbaInfo,
    agentId,
  ]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setEditMode(false);
  }, [form]);

  const handleSave = useCallback(() => {
    setIsSaving(true);
    form.validateFields().then((values) => {
      const associationIds = associationList.map((assoc) => assoc.id);
      const mlsIds = mlsList.map((mls) => mls.id);

      const associationPayload = {
        data: {
          associations: associationIds,
          mlsInfo: mlsIds,
        },
        agent_id: agentId,
        step_completed: "association_info",
      };

      // Check if DBA details exist - if showDba is true, don't send dba_name, else send null
      const officePayload = {
        data: {
          address: values.address,
          apt: values.apt,
          state: values.state,
          city: values.city,
          zipcode: values.zipcode,
          team_name: null,
          ...(showDba ? {} : { dba_name: null }), // Don't send dba_name if showDba is true, send null if false
        },
        agent_id: agentId,
        step_completed: "office_info",
      };

      const dbaPayload =
        showDba && dbaPayloadFunction ? dbaPayloadFunction() : null;

      // Chain the callbacks to ensure all get APIs are called
      updateOnboardingInfo({
        payload: associationPayload,
        callback: () => {
          // After association update, update office info
          updateOnboardingInfo({
            payload: officePayload,
            callback: () => {
              // After office update, update DBA if needed
              if (showDba && dbaPayload) {
                updateOnboardingInfo({
                  payload: dbaPayload,
                  callback: () => {
                    // After all updates, refresh all data
                    getOnboardingAssociationInfo({
                      agentId: agentId,
                      step: "association_info",
                    });
                    getOnboardingOfficeInfo({
                      agentId: agentId,
                      step: "office_info",
                    });
                    getOnboardingDbaInfo({
                      agentId: agentId,
                      step: "dba_info",
                    });
                    setEditMode(false);
                    setIsSaving(false);
                    setShowSuccessMessage(true);
                  },
                });
              } else {
                // If no DBA, just refresh all data
                getOnboardingAssociationInfo({
                  agentId: agentId,
                  step: "association_info",
                });
                getOnboardingOfficeInfo({
                  agentId: agentId,
                  step: "office_info",
                });
                setEditMode(false);
                setIsSaving(false);
                setShowSuccessMessage(true);
              }
            },
          });
        },
      });
    }).catch((error) => {
      console.error("Validation failed:", error);
      setIsSaving(false);
    });
  }, [
    form,
    associationList,
    mlsList,
    agentId,
    showDba,
    dbaPayloadFunction,
    updateOnboardingInfo,
    getOnboardingAssociationInfo,
    getOnboardingOfficeInfo,
    getOnboardingDbaInfo,
  ]);

  const handleAssociationChange = useCallback(
    (value, option) => {
      const selectedAssociation = associationOptions.find(
        (assoc) => assoc.association_name === value
      );

      if (selectedAssociation) {
        const newAssoc = {
          id: selectedAssociation.id,
          association_name: value,
        };
        setAssociationList([newAssoc]);
      }
    },
    [associationOptions]
  );

  const handleMlsChange = useCallback(
    (value, option) => {
      const selectedMls = mlsOptions.find(
        (mls) => (mls.displayValue || mls.short_name) === value
      );

      if (selectedMls) {
        const newMls = {
          id: selectedMls.id,
          short_name: value,
        };
        setMlsList([newMls]);
      }
    },
    [mlsOptions]
  );

  const removeItem = useCallback(
    (value, type) => {
      if (type === "association") {
        setAssociationList(
          associationList.filter((item) => item.association_name !== value)
        );
      } else {
        setMlsList(mlsList.filter((item) => item.short_name !== value));
      }
    },
    [associationList, mlsList]
  );

  const handleAddressChange = useCallback((e) => {
    // Handle manual input changes
    const value = e.target.value;
    form.setFieldsValue({ address: value });
  }, [form]);

  const handleAddressSelect = useCallback((location) => {
    
    // Try different ways to extract zipcode
    const zipcode = get(location, "postal_code.long_name") || 
                    get(location, "postal_code.short_name") ||
                    get(location, "postal_code") ||
                    "";

    const selectedAddress = {
      address: get(location, "address"),
      city: get(location, "locality.long_name"),
      state: get(location, "administrative_area_level_1.short_name"),
      zipcode: zipcode,
    };


    // Update form with the selected address data
    form.setFieldsValue({
      address: selectedAddress.address,
      city: selectedAddress.city,
      state: selectedAddress.state,
      zipcode: selectedAddress.zipcode,
    });

  }, [form]);

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  return (
    <div>
      {showSuccessMessage && (
        <SuccessMessage>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.3333 4L6 11.3333L2.66667 8"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Onboarding details has been updated
        </SuccessMessage>
      )}
      <Form form={form} layout="vertical">
        <SectionContainer>
          <HeaderRow>
            <HeaderContent>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.75 9.75C6.75 9.33579 7.08579 9 7.5 9H16.5C16.9142 9 17.25 9.33579 17.25 9.75C17.25 10.1642 16.9142 10.5 16.5 10.5H7.5C7.08579 10.5 6.75 10.1642 6.75 9.75ZM7.5 13.5H16.5C16.9142 13.5 17.25 13.1642 17.25 12.75C17.25 12.3358 16.9142 12 16.5 12H7.5C7.08579 12 6.75 12.3358 6.75 12.75C6.75 13.1642 7.08579 13.5 7.5 13.5ZM21.75 5.25V19.5C21.7499 19.7599 21.6152 20.0012 21.394 20.1378C21.1729 20.2743 20.8968 20.2866 20.6644 20.1703L18 18.8381L15.3356 20.1703C15.1244 20.276 14.8756 20.276 14.6644 20.1703L12 18.8381L9.33562 20.1703C9.12436 20.276 8.87564 20.276 8.66438 20.1703L6 18.8381L3.33562 20.1703C3.1032 20.2866 2.82712 20.2743 2.60597 20.1378C2.38483 20.0012 2.25014 19.7599 2.25 19.5V5.25C2.25 4.42157 2.92157 3.75 3.75 3.75H20.25C21.0784 3.75 21.75 4.42157 21.75 5.25ZM20.25 5.25H3.75V18.2869L5.66437 17.3288C5.87564 17.223 6.12436 17.223 6.33563 17.3288L9 18.6619L11.6644 17.3288C11.8756 17.223 12.1244 17.223 12.3356 17.3288L15 18.6619L17.6644 17.3288C17.8756 17.223 18.1244 17.223 18.3356 17.3288L20.25 18.2869V5.25Z"
                  fill="#111827"
                />
              </svg>
              <SectionTitle>Business Information</SectionTitle>
            </HeaderContent>
            {!editMode ? (
              <Button
                type="default"
                style={{
                  border: "1px solid #6366F1",
                  borderRadius: "8px",
                  background: "#6366F1",
                  color: "white",
                  fontWeight: 500,
                  height: "36px",
                  padding: "0 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onClick={() => setEditMode(true)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.41421 3.25253C6.19526 4.03358 6.19526 5.29991 5.41421 6.08096C4.63316 6.86201 3.36683 6.86201 2.58579 6.08096C1.80474 5.29991 1.80474 4.03358 2.58579 3.25253C3.36683 2.47149 4.63316 2.47149 5.41421 3.25253"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.3333 4.66659H6"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.4142 9.91904C14.1953 10.7001 14.1953 11.9664 13.4142 12.7475C12.6332 13.5285 11.3668 13.5285 10.5858 12.7475C9.80474 11.9664 9.80474 10.7001 10.5858 9.91904C11.3668 9.13799 12.6332 9.13799 13.4142 9.91904"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2.6665 11.3333H9.99984"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Edit
              </Button>
            ) : (
              <Space>
                <Button type="text" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type="default"
                  disabled={isSaving}
                  style={{
                    borderRadius: "8px",
                    border: "1px solid #6366F1",
                    background: "#6366F1",
                    boxShadow:
                      "0 1px 2px 0 rgba(16, 24, 40, 0.04), 0 1px 2px 0 rgba(16, 24, 40, 0.04)",
                    color: "white",
                    fontWeight: 500,
                    height: "36px",
                    padding: "0 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onClick={handleSave}
                >
                  {isSaving ? (
                    <>
                      <LoadingOutlined style={{ color: "white", marginRight: "8px" }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: "8px", position: "relative", top: "3px"}}>
                        <path d="M10.6663 12.6667H11.9997C13.4724 12.6667 14.6663 11.4728 14.6663 10V10C14.6663 8.52725 13.4724 7.33334 11.9997 7.33334V7.33334C11.9997 5.1242 10.2088 3.33334 7.99967 3.33334V3.33334C6.2878 3.33418 4.76634 4.42462 4.21554 6.04546C2.56745 6.26593 1.33598 7.67057 1.33301 9.33334H1.33301C1.33301 11.1743 2.82539 12.6667 4.66634 12.6667H5.33301" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.33366 10.6667L8.00033 9.33334L6.66699 10.6667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8.00033 12.6667V9.33334" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Save
                    </>
                  )}
                </Button>
              </Space>
            )}
          </HeaderRow>

          <Box>
            <Group>
              <Title
                level={5}
                style={{ marginBottom: 8, fontWeight: 500, fontSize: 16 }}
              >
                MLS Details
              </Title>

              <StyledFormItem label="Association name">
                <Select
                  showSearch
                  placeholder=""
                  optionFilterProp="children"
                  disabled={!editMode}
                  suffixIcon={<SearchOutlined />}
                  onSelect={handleAssociationChange}
                  onSearch={handleAssociationSearch}
                  loading={loadingAssociations}
                  filterOption={false}
                  notFoundContent={
                    loadingAssociations ? "Loading..." : "No associations found"
                  }
                  onPopupScroll={handleAssociationDropdownScroll}
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                      {isLoadingMoreAssociations && (
                        <div style={{ padding: "8px", textAlign: "center" }}>
                          Loading more...
                        </div>
                      )}
                    </div>
                  )}
                >
                  {Array.isArray(associationOptions) &&
                    associationOptions.map((association) => (
                      <Option
                        key={association.id}
                        value={association.association_name}
                      >
                        {association.association_name}
                      </Option>
                    ))}
                </Select>
                <div
                  style={{ marginTop: 8, display: "flex", flexWrap: "wrap" }}
                >
                  {associationList.map((item) => (
                    <StyledTag key={item.id}>
                      {item.association_name}
                      <CloseOutlined
                        onClick={() =>
                          removeItem(item.association_name, "association")
                        }
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      />
                    </StyledTag>
                  ))}
                </div>
              </StyledFormItem>

              <StyledFormItem label="MLS name">
                <Select
                  showSearch
                  placeholder=""
                  optionFilterProp="children"
                  disabled={!editMode}
                  suffixIcon={<SearchOutlined />}
                  onSelect={handleMlsChange}
                  onSearch={handleMlsSearch}
                  loading={loadingMls}
                  filterOption={false}
                  notFoundContent={loadingMls ? "Loading..." : "No MLS found"}
                >
                  {Array.isArray(mlsOptions) &&
                    mlsOptions.map((mls) => (
                      <Option
                        key={mls.id}
                        value={mls.displayValue || mls.short_name}
                      >
                        {mls.displayValue || mls.short_name}
                      </Option>
                    ))}
                </Select>
                <div
                  style={{ marginTop: 8, display: "flex", flexWrap: "wrap" }}
                >
                  {mlsList.map((item) => (
                    <StyledTag key={item.id}>
                      {item.short_name}
                      <CloseOutlined
                        onClick={() => removeItem(item.short_name, "mls")}
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      />
                    </StyledTag>
                  ))}
                </div>
              </StyledFormItem>
            </Group>
          </Box>

          <Box>
            <Group>
              <Title
                level={5}
                style={{ marginBottom: 8, fontWeight: 500, fontSize: 16 }}
              >
                Office address
              </Title>
              <StyledFormItem label="Office address" name="address">
                <Form.Item name="address" noStyle>
                  <GoogleAutoComplete
                    id="office-address"
                    types={["street_address"]}
                    name="office-address"
                    onChange={handleAddressChange}
                    location={(location) => {
                      console.log('location', location);
                      
                      const extractStateFromAddress = (addressStr) => {
                        if (!addressStr) return '';
                        
                        const parts = addressStr.split(',').map(part => part.trim());
                        
                        for (let i = 0; i < parts.length; i++) {
                          const part = parts[i];
                          const stateMatch = part.match(/^([A-Z]{2,3})(\s+\d{5}(-\d{4})?)?$/);
                          if (stateMatch) {
                            return stateMatch[1];
                          }
                        }
                        return '';
                      };

                      const extractCityFromAddress = (addressStr) => {
                        if (!addressStr) return '';
                        
                        const parts = addressStr.split(',').map(part => part.trim());
                        
                        if (parts.length >= 2) {
                          return parts[1];
                        }
                        return '';
                      };

                      const extractZipcodeFromAddress = (addressStr) => {
                        if (!addressStr) return '';
                        
                        const parts = addressStr.split(',').map(part => part.trim());
                        
                        for (let i = 0; i < parts.length; i++) {
                          const part = parts[i];
                          const zipMatch = part.match(/\d{5}(-\d{4})?/);
                          if (zipMatch) {
                            return zipMatch[0];
                          }
                        }
                        return '';
                      };

                      const streetNumber = location?.street_number?.long_name || '';
                      const route = location?.route?.long_name || '';
                      const streetAddress = `${streetNumber} ${route}`.trim();

                      let state = '';
                      if (location?.administrative_area_level_1?.long_name) {
                        state = location.administrative_area_level_1.long_name;
                      } else if (location?.address) {
                        state = extractStateFromAddress(location.address);
                      }

                      let city = '';
                      if (location?.locality?.long_name) {
                        city = location.locality.long_name;
                      } else if (location?.address) {
                        city = extractCityFromAddress(location.address);
                      }

                      let zipcode = '';
                      if (location?.postal_code?.long_name) {
                        zipcode = location.postal_code.long_name;
                      } else if (location?.address) {
                        zipcode = extractZipcodeFromAddress(location.address);
                      }

                      const selectedAddress = {
                        address: streetAddress,
                        city: city,
                        state: state,
                        zipcode: zipcode
                      };

                      
                      form.setFieldsValue({
                        address: selectedAddress.address,
                        city: selectedAddress.city,
                        state: selectedAddress.state,
                        zipcode: selectedAddress.zipcode
                      });
                    }}
                    placeholder=""
                    disabled={!editMode}
                    styles={{
                      height: "40px",
                      borderRadius: "8px", 
                      background: "white",
                      fontSize: "14px",
                      border: "1px solid #d9d9d9",
                      padding: "8px 12px",
                    }}
                    restrict={{
                      lat: 37.09024,
                      lng: -95.712891,
                      south_west: {
                        lat: 25.82,
                        lng: -124.39,
                      },
                      north_east: {
                        lat: 49.38,
                        lng: -66.94,
                      },
                      address: "United States",
                      country: {
                        long_name: "United States",
                        short_name: "US",
                        types: [
                          "country",
                          "political",
                        ],
                      },
                    }}
                  />
                </Form.Item>
              </StyledFormItem>

              <StyledFormItem label="Suite/Apt">
                <Form.Item name="apt" noStyle>
                  <Input
                    placeholder=""
                    disabled={!editMode}
                  />
                </Form.Item>
              </StyledFormItem>

              <Grid3Col>
                <StyledFormItem label="City">
                  <Form.Item 
                    name="city" 
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a city'
                      }
                    ]}
                  >
                    <Input
                      placeholder=""
                      disabled={!editMode}
                      value={form.getFieldValue('city')}
                      onChange={(e) => {
                        form.setFieldsValue({
                          city: e.target.value
                        });
                      }}
                    />
                  </Form.Item>
                </StyledFormItem>

                <StyledFormItem label="State">
                  <Form.Item name="state" noStyle>
                    <Input
                      placeholder=""
                      disabled={!editMode}
                    />
                  </Form.Item>
                </StyledFormItem>

                <StyledFormItem label="Zip code">
                  <Form.Item name="zipcode" noStyle>
                    <Input
                      placeholder=""
                      disabled={!editMode}
                    />
                  </Form.Item>
                </StyledFormItem>
              </Grid3Col>
            </Group>
          </Box>

          <Box>
            <Group>
              <Title
                level={5}
                style={{ marginBottom: 8, fontWeight: 500, fontSize: 16 }}
              >
                Do you want to file a DBA?
              </Title>
              <Radio.Group
                value={showDba ? "yes" : "no"}
                onChange={(e) => setShowDba(e.target.value === "yes")}
                style={{ display: "flex", gap: 32 }}
                disabled={!editMode || !isRoleAllowed()}
              >
                <Radio value="yes">Yes</Radio>
                <Radio value="no">No</Radio>
              </Radio.Group>

              {showDba && (
                <DbaSection
                  disabled={!editMode}
                  onPayloadReady={handleDbaPayloadReady}
                  agentId={agentId}
                  agentRole={agentRole}
                />
              )}
            </Group>
          </Box>
        </SectionContainer>
      </Form>
    </div>
  );
};

export default container(BusinessInfoSection);
